import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { api, isAccessTokenExpired } from '../../lib/api';
import { clearSelectedStandortId } from '../../lib/standortState';
import { fachBezeichnungenMerken, kalenderEinstellungenMerken } from '../../lib/settings';
import { AuthContext } from './auth-context';


// Benutzer der Oberfläche — bewusst ohne Rollen: was jemand darf, liefert der
// Server als Permissions (components/rbac, RBAC Phase 5).
export interface User {
  id: string;
  email: string;
  name: string;
  tenantIds: string[];
  mandantId?: number;
  /** Bevorzugter Standort für die Vorauswahl. */
  standortId?: number;
}

// Legacy-Backend-Benutzer-Interface
interface LegacyBackendUser {
  id: number;
  email: string;
  vorname: string;
  nachname: string;
  rolle: string;
  mandant_id?: number;
  standort_id?: number;
  alle_standorte?: boolean;
  standorte_ids?: number[];
  /** JSON-Objekt mit String-Keys (int-IDs) auf StandortRole. */
  roles_by_standort?: Record<string, string>;
  is_super_admin?: boolean;
  aktiv: boolean;
}

/**
 * Lesbarer Gerätename für die Geräteliste („Chrome · Windows“) — vorher stand
 * dort „Unbekanntes Gerät“, weil das Office keinen mitschickte (T-0218).
 */
const geraeteName = (): string => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Firefox\//.test(ua) ? 'Firefox'
      : /Chrome\//.test(ua) ? 'Chrome'
        : /Safari\//.test(ua) ? 'Safari'
          : 'Browser';
  const system = /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad/.test(ua) ? 'iOS'
      : /Android/.test(ua) ? 'Android'
        : /Mac OS X/.test(ua) ? 'macOS'
          : /Linux/.test(ua) ? 'Linux'
            : 'unbekannt';
  return `${browser} · ${system}`;
};

interface FastApiAuthMeResponse {
  id: number;
  account_id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  isTeacher?: boolean;
  /** Bezeichnung für „Fach“ aus den Einstellungen (T-0220). */
  fachBezeichnung?: { singular?: string; plural?: string } | null;
  kalender?: { startHour?: number; endHour?: number; timeInterval?: number; timezone?: string } | null;
}

interface FastApiTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_at: string;
  session_expires_at?: string | null;
}

interface BackendMfaRequiredResponse {
  requires_mfa: true;
  requires_setup?: boolean;
  challenge_id: string | null;
  challenge_expires_at?: string | null;
  methods?: {
    totp?: boolean;
    email?: boolean;
  };
  email_code_sent?: boolean;
}

type BackendLoginResponse = FastApiTokenResponse | { user: LegacyBackendUser; token: string; expires_at: string; session_expires_at?: string; remember?: boolean };
type BackendUserResponse = FastApiAuthMeResponse | LegacyBackendUser;

export interface MfaPendingState {
  challengeId: string | null;
  requiresSetup: boolean;
  rememberMe: boolean;
  methods: {
    totp: boolean;
    email: boolean;
  };
}

export interface MfaSetupResponse {
  setup: {
    secret: string;
    otpauthUrl: string;
    issuer: string;
    accountName: string;
  };
  status: {
    enabled: boolean;
    totpEnabled: boolean;
    emailOtpEnabled: boolean;
    hasSecret: boolean;
  };
  required: boolean;
}

const toMfaSetupResponse = (data: Record<string, unknown>): MfaSetupResponse => {
  const secret = typeof data.secret === 'string' ? data.secret : '';
  const provisioningUri = typeof data.provisioning_uri === 'string' ? data.provisioning_uri : '';
  const isEnabled = data.is_enabled === true;
  return {
    setup: {
      secret,
      otpauthUrl: provisioningUri,
      issuer: 'sidigo-fastapi',
      accountName: '',
    },
    status: {
      enabled: isEnabled,
      totpEnabled: isEnabled,
      emailOtpEnabled: false,
      hasSecret: secret !== '',
    },
    required: data.is_required === true,
  };
};

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState<MfaPendingState | null>(null);
  const sessionTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const TOKEN_STORAGE_KEY = 'sidigo-token-storage'; // 'local' | 'session'
  const REFRESH_TOKEN_KEY = 'sidigo-refresh-token';
  const SESSION_EXPIRES_KEY = 'sidigo-session-expires-at';
  const SESSION_EXPIRES_EXPLICIT_KEY = 'sidigo-session-explicit';
  const AUTH_EXPIRED_EVENT = 'sidigo:auth_expired';

  const clearSessionTimer = () => {
    if (sessionTimerRef.current) {
      window.clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  };

  const scheduleSessionExpiry = (sessionExpiresAtIso: string | undefined | null) => {
    if (typeof window === 'undefined') return;
    clearSessionTimer();
    if (!sessionExpiresAtIso) return;
    const ts = Date.parse(sessionExpiresAtIso);
    if (!Number.isFinite(ts)) return;
    const ms = ts - Date.now();
    if (ms <= 0) {
      // already expired
      void logout();
      return;
    }
    sessionTimerRef.current = window.setTimeout(() => {
      void logout();
    }, ms);
  };

  const getStoredToken = (): string | null => {
    const local = localStorage.getItem('sidigo-token');
    if (local) return local;
    return sessionStorage.getItem('sidigo-token');
  };

  const getStoredRefreshToken = (): string | null => {
    const local = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (local) return local;
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  };

  const isFastApiAuthMeResponse = (value: BackendUserResponse): value is FastApiAuthMeResponse =>
    typeof (value as FastApiAuthMeResponse).first_name === 'string' &&
    Array.isArray((value as FastApiAuthMeResponse).roles);

  const isFastApiTokenResponse = (value: BackendLoginResponse): value is FastApiTokenResponse =>
    typeof (value as FastApiTokenResponse).access_token === 'string' &&
    typeof (value as FastApiTokenResponse).refresh_token === 'string';

  const clearStoredAuthData = () => {
    try { localStorage.removeItem('sidigo-token'); } catch { /* ignore */ }
    try { sessionStorage.removeItem('sidigo-token'); } catch { /* ignore */ }
    try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* ignore */ }
    try { sessionStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(SESSION_EXPIRES_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(SESSION_EXPIRES_EXPLICIT_KEY); } catch { /* ignore */ }
  };

  const persistTokens = (
    accessToken: string,
    refreshToken: string | null | undefined,
    rememberMe: boolean,
    sessionExpiresAt?: string | null,
  ) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, rememberMe ? 'local' : 'session');
    if (rememberMe) {
      localStorage.setItem('sidigo-token', accessToken);
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      try { sessionStorage.removeItem('sidigo-token'); } catch { /* ignore */ }
      try { sessionStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* ignore */ }
    } else {
      sessionStorage.setItem('sidigo-token', accessToken);
      if (refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      try { localStorage.removeItem('sidigo-token'); } catch { /* ignore */ }
      try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* ignore */ }
    }
    if (sessionExpiresAt && sessionExpiresAt.trim() !== '') {
      localStorage.setItem(SESSION_EXPIRES_KEY, sessionExpiresAt);
      localStorage.setItem(SESSION_EXPIRES_EXPLICIT_KEY, '1');
      scheduleSessionExpiry(sessionExpiresAt);
    } else {
      localStorage.removeItem(SESSION_EXPIRES_KEY);
      localStorage.removeItem(SESSION_EXPIRES_EXPLICIT_KEY);
      clearSessionTimer();
    }
  };

  // Backend-Benutzer zu Frontend-Benutzer konvertieren (ohne Rollen)
  const convertBackendUser = (backendUser: BackendUserResponse): User => {
    if (isFastApiAuthMeResponse(backendUser)) {
      return {
        id: backendUser.id.toString(),
        email: backendUser.email,
        name: `${backendUser.first_name} ${backendUser.last_name}`.trim() || backendUser.email,
        tenantIds: [],
      };
    }
    return {
      id: backendUser.id.toString(),
      email: backendUser.email,
      name: `${backendUser.vorname} ${backendUser.nachname}`.trim() || backendUser.email,
      tenantIds: backendUser.mandant_id ? [backendUser.mandant_id.toString()] : [],
      mandantId: backendUser.mandant_id,
      standortId: backendUser.standort_id,
    };
  };

  // Benutzerdaten vom Backend abrufen
  const fetchUserData = async (): Promise<User | null> => {
    try {
      const backendUser = await api.get<BackendUserResponse>('/api/auth/me');
      const bezeichnung = (backendUser as FastApiAuthMeResponse).fachBezeichnung;
      if (bezeichnung) fachBezeichnungenMerken(bezeichnung.singular, bezeichnung.plural);
      kalenderEinstellungenMerken((backendUser as FastApiAuthMeResponse).kalender);
      return convertBackendUser(backendUser);
    } catch (error) {
      console.error('Fehler beim Abrufen der Benutzerdaten:', error);
      return null;
    }
  };

  // Token validieren und Benutzerdaten abrufen
  const checkAuth = async () => {
    const sessionExpiresAt = localStorage.getItem(SESSION_EXPIRES_KEY);
    const explicitSessionExpiry = localStorage.getItem(SESSION_EXPIRES_EXPLICIT_KEY) === '1';
    if (sessionExpiresAt && explicitSessionExpiry) {
      scheduleSessionExpiry(sessionExpiresAt);
    } else {
      localStorage.removeItem(SESSION_EXPIRES_KEY);
      localStorage.removeItem(SESSION_EXPIRES_EXPLICIT_KEY);
    }

    const token = getStoredToken();
    const refreshToken = getStoredRefreshToken();
    try {
      // Ein abgelaufener Access-Token ist genauso unbrauchbar wie gar keiner.
      // Vorher galt nur `!token` als Anlass zum Erneuern, weshalb die App nach
      // jeder Pause erst in einen 401 lief statt vorher zu refreshen.
      const hasFreshToken = !!token && !isAccessTokenExpired(token);
      let hasUsableAuth = hasFreshToken;
      if (!hasFreshToken && refreshToken) {
        try {
          const refreshed = await api.post<FastApiTokenResponse>('/api/auth/refresh', {
            refresh_token: refreshToken,
          });
          const preferLocal = !!localStorage.getItem(REFRESH_TOKEN_KEY);
          const refreshedSessionExpiresAt = typeof refreshed.session_expires_at === 'string'
            ? refreshed.session_expires_at
            : null;
          persistTokens(
            refreshed.access_token,
            refreshed.refresh_token || refreshToken,
            preferLocal,
            refreshedSessionExpiresAt,
          );
          hasUsableAuth = true;
        } catch {
          hasUsableAuth = false;
        }
      }

      // Der API-Client loescht die Tokens nur, wenn das Backend den
      // Refresh-Token wirklich abgelehnt hat. Liegen sie noch im Storage, war
      // der Fehlschlag voruebergehend (Netzwerk, 5xx) — dann zeigen wir zwar
      // den Login, werfen die Sitzung aber nicht zusaetzlich lokal weg. Ein
      // Reload mit funktionierender Verbindung meldet den Benutzer wieder an.
      const sessionStillStored = () => !!getStoredRefreshToken();

      if (!hasUsableAuth) {
        if (!sessionStillStored()) clearStoredAuthData();
        setUser(null);
        if (mountedRef.current) setIsLoading(false);
        return;
      }

      const userData = await fetchUserData();
      if (userData) {
        setUser(userData);
      } else {
        if (!sessionStillStored()) clearStoredAuthData();
        setUser(null);
      }
    } catch (error) {
      console.error('Authentifizierungsfehler:', error);
      if (getStoredRefreshToken()) {
        setUser(null);
      } else {
        clearStoredAuthData();
        setUser(null);
      }
    }
    if (mountedRef.current) setIsLoading(false);
  };

  // Check for existing token on mount
  useEffect(() => {
    mountedRef.current = true;
    checkAuth();
    return () => {
      mountedRef.current = false;
      clearSessionTimer();
    };
  }, []);

  // Login function
  const login = async (email: string, password: string, rememberMe = true): Promise<{ requiresMfa: boolean }> => {
    setIsLoading(true);
    try {
      const response = await api.post<BackendLoginResponse | BackendMfaRequiredResponse>('/api/auth/login', {
        email,
        password,
        remember: rememberMe,
        device_id: geraeteName(),
      });

      if ((response as BackendMfaRequiredResponse).requires_mfa) {
        const mfaRes = response as BackendMfaRequiredResponse;
        setMfaPending({
          challengeId: mfaRes.challenge_id,
          requiresSetup: !!mfaRes.requires_setup,
          rememberMe,
          methods: {
            totp: !!mfaRes.methods?.totp,
            email: !!mfaRes.methods?.email,
          },
        });
        toast.info(mfaRes.requires_setup ? 'MFA-Einrichtung erforderlich' : 'MFA-Bestätigung erforderlich');
        return { requiresMfa: true };
      }

      const authRes = response as BackendLoginResponse;
      const accessToken = isFastApiTokenResponse(authRes) ? authRes.access_token : authRes.token;
      const refreshToken = isFastApiTokenResponse(authRes) ? authRes.refresh_token : null;
      const sessionExpiresAt = typeof (authRes as Record<string, unknown>).session_expires_at === 'string'
        ? String((authRes as Record<string, unknown>).session_expires_at)
        : null;

      persistTokens(accessToken, refreshToken, rememberMe, sessionExpiresAt);

      // Benutzerdaten setzen (FastAPI liefert sie über /auth/me)
      const userData = isFastApiTokenResponse(authRes)
        ? await fetchUserData()
        : convertBackendUser(authRes.user);
      if (!userData) {
        throw new Error('Benutzerdaten konnten nach dem Login nicht geladen werden');
      }
      setUser(userData);
      setMfaPending(null);
      
      toast.success('Erfolgreich angemeldet');
      return { requiresMfa: false };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyMfa = async (method: 'totp' | 'email', code: string) => {
    if (!mfaPending?.challengeId) {
      throw new Error('Keine MFA-Challenge aktiv');
    }
    const response = await api.post<BackendLoginResponse>('/api/auth/mfa/verify', {
      challenge_id: mfaPending.challengeId,
      method,
      code,
    });
    await finishMfaLogin(response, mfaPending.rememberMe);
    toast.success('MFA erfolgreich bestätigt');
  };

  // Von der Richtlinie erzwungene Einrichtung: läuft mit dem Einrichtungs-Token
  // aus dem Login, weil es noch keine Sitzung (Access-Token) gibt.
  const loadMfaEnrollment = async (): Promise<MfaSetupResponse> => {
    if (!mfaPending?.challengeId) {
      throw new Error('Keine MFA-Challenge aktiv');
    }
    const data = await api.post<Record<string, unknown>>('/api/auth/mfa/enroll', {
      challenge_id: mfaPending.challengeId,
    });
    return toMfaSetupResponse(data);
  };

  const completeMfaEnrollment = async (totpCode: string) => {
    if (!mfaPending?.challengeId) {
      throw new Error('Keine MFA-Challenge aktiv');
    }
    const response = await api.post<BackendLoginResponse>('/api/auth/mfa/enroll/confirm', {
      challenge_id: mfaPending.challengeId,
      code: totpCode,
    });
    await finishMfaLogin(response, mfaPending.rememberMe);
    toast.success('MFA wurde eingerichtet');
  };

  const finishMfaLogin = async (response: BackendLoginResponse, rememberMe: boolean) => {
    const accessToken = (response as Record<string, unknown>).token || (response as Record<string, unknown>).access_token;
    const refreshToken = (response as Record<string, unknown>).refresh_token;
    const sessionExpiresAt = (response as Record<string, unknown>).session_expires_at;
    if (typeof accessToken !== 'string' || accessToken.trim() === '') {
      throw new Error('MFA-Antwort enthält kein Access-Token');
    }
    persistTokens(
      accessToken,
      typeof refreshToken === 'string' ? refreshToken : getStoredRefreshToken(),
      rememberMe,
      typeof sessionExpiresAt === 'string' ? sessionExpiresAt : null,
    );

    const legacyUser = (response as Record<string, unknown>).user as LegacyBackendUser | undefined;
    const userData = legacyUser ? convertBackendUser(legacyUser) : await fetchUserData();
    if (!userData) {
      throw new Error('Benutzerdaten konnten nach der MFA-Bestätigung nicht geladen werden');
    }
    setUser(userData);
    setMfaPending(null);
  };

  const resendMfaEmail = async () => {
    if (!mfaPending?.challengeId) {
      throw new Error('Keine MFA-Challenge aktiv');
    }
    await api.post('/api/auth/mfa/email/send', { challenge_id: mfaPending.challengeId });
    toast.success('E-Mail-Code wurde versendet');
  };

  const loadMfaSetup = async (): Promise<MfaSetupResponse> => {
    const data = await api.get<Record<string, unknown>>('/api/auth/mfa/setup');
    return toMfaSetupResponse(data);
  };

  const enableMfa = async (totpCode: string) => {
    await api.post('/api/auth/mfa/enable', { code: totpCode });
    await refreshUser();
    setMfaPending(null);
    toast.success('MFA wurde aktiviert');
  };

  const disableMfa = async (totpCode: string) => {
    await api.post('/api/auth/mfa/disable', { code: totpCode });
    await refreshUser();
    toast.success('MFA wurde deaktiviert');
  };

  // Register function (falls vom Backend unterstützt)
  const register = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    try {
      // Namen in Vor- und Nachname aufteilen
      const nameParts = name.trim().split(' ');
      const vorname = nameParts[0] || '';
      const nachname = nameParts.slice(1).join(' ') || '';

      const response = await api.post<Record<string, unknown>>('/api/auth/register', {
        email,
        password,
        first_name: vorname,
        last_name: nachname,
      });

      const accessToken = typeof response.access_token === 'string' ? response.access_token : null;
      const refreshToken = typeof response.refresh_token === 'string' ? response.refresh_token : null;
      const sessionExpiresAt = typeof response.session_expires_at === 'string' ? response.session_expires_at : null;
      if (!accessToken) {
        throw new Error('Registrierung erfolgreich, aber kein Access-Token erhalten');
      }

      persistTokens(accessToken, refreshToken, true, sessionExpiresAt ?? null);
      const userData = await fetchUserData();
      if (!userData) {
        throw new Error('Benutzerdaten konnten nach der Registrierung nicht geladen werden');
      }
      setUser(userData);
      
      toast.success('Konto erfolgreich erstellt');
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registrierung fehlgeschlagen';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Backend-Logout aufrufen
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        await api.post('/api/auth/logout', { refresh_token: refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Trotz Fehler lokal abmelden
    } finally {
      if (user?.id) {
        clearSelectedStandortId(user.id);
      }
      clearStoredAuthData();
      clearSessionTimer();
      setUser(null);
      setMfaPending(null);
      toast.info('Du wurdest abgemeldet');
    }
  };

  // Benutzerdaten aktualisieren
  const refreshUser = async () => {
    if (user) {
      const updatedUser = await fetchUserData();
      if (updatedUser) {
        setUser(updatedUser);
      }
    }
  };

  // Globaler Auth-Expired Listener (aus api.ts bei 401/Refresh-Fail)
  useEffect(() => {
    const handler = () => {
      // State-Updates defensiv asynchron ausführen, um React-Render-Phasen nicht zu stören
      queueMicrotask(() => {
        clearStoredAuthData();
        clearSessionTimer();
        if (mountedRef.current) setUser(null);
        if (mountedRef.current) setMfaPending(null);
      });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler as any);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler as any);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      mfaPending,
      login,
      verifyMfa,
      resendMfaEmail,
      loadMfaSetup,
      enableMfa,
      disableMfa,
      loadMfaEnrollment,
      completeMfaEnrollment,
      register,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
